import { getFieldErrors, isApiError, readApiError } from '@/services/api';
import type {
    AccessJustificationPayload,
    CallerConfirmationDetails,
    CallerConfirmationPayload,
} from '../types/agreementAccess.types';

export const ACCESS_CONFIRMATION_ALREADY_RECORDED = 'access_confirmation_already_recorded';

export type ConfirmationField = keyof CallerConfirmationPayload;

export interface ConfirmationDetailRow {
    field: ConfirmationField;
    detail: keyof Pick<CallerConfirmationDetails, 'fullName' | 'addressLine1' | 'postcode'>;
    label: string;
}

export const CONFIRMATION_DETAIL_ROWS: readonly ConfirmationDetailRow[] = [
    { field: 'nameConfirmed', detail: 'fullName', label: 'Full name' },
    { field: 'addressConfirmed', detail: 'addressLine1', label: 'First line of address' },
    { field: 'postcodeConfirmed', detail: 'postcode', label: 'Postcode' },
];

export type AccessPromptField = ConfirmationField | keyof AccessJustificationPayload;

const WIRE_TO_FORM_FIELD: Record<string, AccessPromptField> = {
    case_reference: 'caseReference',
    reason: 'reason',
    name_confirmed: 'nameConfirmed',
    address_confirmed: 'addressConfirmed',
    postcode_confirmed: 'postcodeConfirmed',
};

const FALLBACK_MESSAGE = 'Failed to record your access. Please try again.';

export interface ConfirmationSubmitError {
    fieldErrors: { field: AccessPromptField; message: string }[];
    message: string | null;
}

export function allDetailsConfirmed(values: Partial<CallerConfirmationPayload>): boolean {
    return CONFIRMATION_DETAIL_ROWS.every(({ field }) => values[field] === true);
}

export function isConfirmationAlreadyRecorded(error: unknown): boolean {
    if (!isApiError(error)) return false;

    return error.code === ACCESS_CONFIRMATION_ALREADY_RECORDED || readApiError(error.data).code === ACCESS_CONFIRMATION_ALREADY_RECORDED;
}

export function mapConfirmationSubmitError(error: unknown): ConfirmationSubmitError {
    const wireErrors = getFieldErrors(error);
    const fieldErrors = Object.entries(wireErrors).flatMap(([wireField, messages]) => {
        const field = WIRE_TO_FORM_FIELD[wireField];
        return field && messages[0] ? [{ field, message: messages[0] }] : [];
    });
    const hasUnmappedErrors = Object.keys(wireErrors).some((wireField) => !WIRE_TO_FORM_FIELD[wireField]);

    if (fieldErrors.length > 0 && !hasUnmappedErrors) {
        return { fieldErrors, message: null };
    }

    const message = isApiError(error) && error.data ? readApiError(error.data).message : FALLBACK_MESSAGE;

    return { fieldErrors, message };
}
